<template>
  <div class="product-card rounded-0 my-2">
    <div class="card rounded-0 text-white h-100">
      <!-- Modificar el badge de descuento para que solo aparezca si hay descuento mayor a 0 y el usuario está autenticado -->
      <div v-if="selectedBlister && selectedBlister.discount > 0" class="discount-badge-wrapper">
        <span class="discount-badge p-2">
          -{{ formatDiscount(selectedBlister.discount) }}% OFF
        </span>
      </div>
      <a :href="`/producto/${product.id}`" class="card-img-wrapper rounded-0" :class="{ 'card-img-wrapper--fixed': minHeight }" :style="minHeight ? { height: minHeight, flexBasis: minHeight } : undefined">
        <div class="image-container rounded-0" :class="{ 'second-image-container': !showFirstImage, 'image-container--plain': imageOnly, 'image-container--fixed': minHeight }">
            <div v-if="!imageOnly && fadeImage && !imageLoaded" class="image-skeleton" aria-hidden="true"></div>
            <img
            v-if="!imageOnly && blurBackground && !isFallbackImage"
            :src="imageSrc"
            alt=""
            aria-hidden="true"
            class="card-img-background rounded-0"
            @error="handleImageError"
            >
            <img 
            ref="imgRef"
            :src="imageSrc"
            :alt="product.name" 
            class="card-img rounded-0"
            :class="{ loaded: !fadeImage || imageLoaded, 'second-image': !showFirstImage, 'fallback-image': isFallbackImage }"
            @load="handleImageLoad"
            @error="handleImageError"
            >
        </div>
      </a>
      <div class="card-body bg-secondary p-2 p-md-3 mt-0">
        <div class="d-flex justify-content-center">
          <div class="d-flex flex-column justify-content-center desktop-title-container" style="min-height: 80px; width: 100%;">
            <a :href="`/producto/${product.id}`" class="text-decoration-none">
              <h5 class="product-name mb-0 fw-light fs-4 text-center text-uppercase" v-if="showFirstImage">
                {{ product.genotype }}
              </h5>
              <h5 class="product-name mb-0 fw-light fs-4 text-center text-uppercase" v-else>
                {{ product.name }}
              </h5>
              <p v-if="product.type == 'fem'" class="text-center brand mb-0 dinn-font text-white fs-6">(Fotoperiódica)</p>
              <p v-else-if="product.type == 'auto'" class="text-center brand mb-0 dinn-font text-white fs-6">(Autofloreciente)</p>
            </a>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, defineProps, watch, computed, nextTick } from 'vue'
import { productImageUrls, useImageGallery } from '@/composables/useProductImages'
import { 
  getFirstAvailableBlister, 
} from "@/helpers/blisterUtils";

// Define prop con validación
const props = defineProps({
  blurBackground: {
    type: Boolean,
    default: true
  },
  imageOnly: {
    type: Boolean,
    default: false
  },
  showFirstImage: {
    type: Boolean,
    default: true
  },
  minHeight: {
    type: [Number, String]
  },
  showName: {
    type: Boolean,
    default: true
  },
  fadeImage: {
    type: Boolean,
    default: true
  },
  product: {
    type: Object,
    required: true,
    default: () => ({
      id: null,
      brand_id: '',
      brand: '',
      name: '',
      image: '',
      image_2: null,
      image_3: null,
      description: '',
      blisters: [], // Ensure blisters is always initialized as an array
      flowering_time: 0,
      thc: '',
      cbd: '',
      flavors: '',
      type: '',
      is_national: false
    })
  }
})

const imageLoaded = ref(false)
const imgRef = ref(null)

const selectedBlister = ref(null)

// Home prefiere la segunda imagen; el catálogo comienza por la principal.
const sourceImages = computed(() => productImageUrls(props.product, props.showFirstImage))
const { currentImage, onImageError } = useImageGallery(sourceImages)
const isFallbackImage = computed(() => !currentImage.value)
const imageSrc = computed(() => currentImage.value || '/LOGO.svg')

const checkImageAlreadyLoaded = () => {
  nextTick(() => {
    const img = imgRef.value
    if (img?.complete && img.naturalWidth > 0) {
      imageLoaded.value = true
    }
  })
}

// Watch for product changes to set initial blister with safeguards against undefined
watch(() => props.product, (newProduct) => {
  // Make sure newProduct and blisters exist and blisters is an array
  if (newProduct && newProduct.blisters && Array.isArray(newProduct.blisters) && newProduct.blisters.length) {
    selectedBlister.value = getFirstAvailableBlister(newProduct.blisters);
  } else {
    // Reset selected blister if no valid blisters available
    selectedBlister.value = null
  }
}, { immediate: true })

watch(() => [props.product.id, imageSrc.value], () => {
  if (!props.fadeImage) {
    imageLoaded.value = true
    return
  }
  imageLoaded.value = false
  checkImageAlreadyLoaded()
}, { immediate: true })


const handleImageLoad = () => {
  imageLoaded.value = true
}

const handleImageError = (event) => {
  onImageError(event)
  if (!currentImage.value) imageLoaded.value = true
}
const formatDiscount = (discount) => {
  // Redondear al número entero más cercano y asegurar 2 dígitos
  const roundedDiscount = Math.round(discount);
  return roundedDiscount.toString().padStart(2, '0');
}
</script>

<style scoped>
.card {
  border: none;
  transition: transform 0.2s ease;
  height: 100%;
  position: relative; /* Asegurar que el badge se posicione correctamente */
  background-color: transparent; /* Fondo totalmente transparente */
  margin: 0;
  padding: 0;
}

.card-img-wrapper {
  display: block;
  padding: 0;
  margin: 0;
  text-decoration: none;
  background-color: transparent; /* Fondo totalmente transparente */
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
}
.card-image-wrapper img{
  background-color: transparent; /* Fondo totalmente transparente */
  margin: 0;
  padding: 0;
}

.image-container {
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent; /* Asegurar que el fondo sea transparente */
  margin: 0;
  padding: 0;
  position: relative;
}

.image-container--plain {
  background: transparent;
}

.image-container--plain .card-img {
  object-fit: contain;
}

.card-img-wrapper--fixed {
  flex-shrink: 0;
}

.image-container--fixed {
  height: 100%;
  max-height: none;
}

.image-container--fixed .card-img {
  height: 100%;
}

.second-image-container {
  max-height: 400px;
}

.image-container img {
  width: 100%;
  transition: transform 0.3s ease;
  margin: 0;
  padding: 0;
}

.card-img-background {
  position: absolute;
  inset: -18px;
  width: calc(100% + 36px) !important;
  height: calc(100% + 36px) !important;
  object-fit: cover !important;
  filter: blur(16px) brightness(0.42);
  opacity: 0.95;
  z-index: 0;
  pointer-events: none;
}

.image-container img.second-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: bottom;
}

.card{
  border-radius: 0 !important;
}


.card-img {
  width: 100%;
  height: auto;
  position: relative;
  z-index: 2;
  object-fit: contain;
}

.card-img:not(.loaded) {
  opacity: 0;
  transition: opacity 0.5s ease;
}

.card-img.loaded {
  opacity: 1;
  transition: opacity 0.5s ease;
}

.image-skeleton {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(51, 51, 51, 0.6) 25%, rgba(64, 64, 64, 0.8) 50%, rgba(51, 51, 51, 0.6) 75%);
  background-size: 200% 100%;
  animation: image-skeleton-loading 1.5s infinite;
  z-index: 1;
}

@keyframes image-skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.card-img.second-image {
  object-fit: cover;
  height: 100%;
}

.card-img.fallback-image {
  object-fit: contain !important;
}

@media (max-width: 768px) {
  .product-card {
    margin-bottom: 0.5rem;
    margin-top: 0.5rem !important;
  }
  

  .image-container {
    width: 100%;
    position: relative;
  }
  
  .image-container.second-image-container {
    max-height: 400px;
    min-height: 0 !important; /* Override desktop height on mobile */
  }
  
  .card {
    margin-bottom: 0.5rem;
  }
  
  .product-name {
    font-size: 1.4rem !important; /* Smaller font on mobile */
  }
  
  .desktop-title-container {
    min-height: auto !important; /* Override desktop height on mobile */
  }
}

.brand {
  font-size: 0.75rem;
  color: #808080;
}

.product-name {
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}

.price {
  font-size: 1.1rem;
  font-weight: 600;
}

.eye-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
}

.card:hover .eye-icon {
  opacity: 1;
}

@media (max-width: 400px) {
  .image-container.second-image-container {
    max-height: 400px;
    aspect-ratio: 0;
  }
}
</style>
